/**
 * =========================================================
 * service-hub/api.ts — Service Hub API
 * =========================================================
 * Endpoint-et:
 *   GET  /api/v1/service-hub/catalog       → 4 shërbime
 *   GET  /api/v1/service-hub/subscriptions → abonimet aktuale
 *   POST /api/v1/service-hub/activate      → kërko aktivizim
 * =========================================================
 */

import { apiClient } from "@/lib/api/client";
import { asArray, unwrapApiData } from "@/lib/api/response";

// ── Tipet ─────────────────────────────────────────────────
export interface ServiceCatalogItem {
  service_code:  string;
  name:          string;
  description:   string;
  icon:          string;
  monthly_price: number;
  annual_price:  number;
}

export interface ServiceSubscription {
  id:           number;
  service_code: string;
  plan:         string;
  status:       string;
  valid_until?: string | null;
}

export interface ActivationPayload {
  service_code: string;
  plan:         "monthly" | "annual";
}

export interface ActivationResponse {
  message: string;
  subscription_id: number;
  status: "pending_payment";
  amount_due: string;
  currency: string;
}

export interface PaySubscriptionResponse {
  message: string;
  subscription_id: number;
  status: "pending_admin_approval";
  amount_charged: string;
  business_invoice_id: number;
}

const businessRequestConfig = (businessId?: string | null) =>
  businessId ? { headers: { "X-Business-Id": businessId } } : undefined;

// Status → etiketa + stil
export const SUB_STATUS: Record<string, { label: string; cls: string }> = {
  active:               { label: "Aktiv",         cls: "border-emerald-200 bg-emerald-50 text-emerald-700"  },
  draft:                { label: "Dërguar",        cls: "border-blue-200 bg-blue-50 text-blue-700"          },
  pending_payment:      { label: "Pagesë",         cls: "border-amber-200 bg-amber-50 text-amber-700"       },
  paid:                 { label: "Paguar",         cls: "border-sky-200 bg-sky-50 text-sky-700"             },
  pending_admin_approval:{ label: "Në pritje të aprovimit", cls: "border-violet-200 bg-violet-50 text-violet-700" },
  suspended:            { label: "Pezulluar",      cls: "border-orange-200 bg-orange-50 text-orange-700"    },
  cancelled:            { label: "Anuluar",        cls: "border-red-200 bg-red-50 text-red-700"             },
  expired:              { label: "I skaduar",      cls: "border-slate-200 bg-slate-50 text-slate-700"       },
};

// ── API calls ─────────────────────────────────────────────
export const getServiceCatalog = async (): Promise<ServiceCatalogItem[]> => {
  const r = await apiClient.get<unknown>("/service-hub/catalog");
  return asArray<ServiceCatalogItem>(r.data).map((item) => ({
    ...item,
    monthly_price: Number(item.monthly_price),
    annual_price: Number(item.annual_price),
  }));
};

export const getMySubscriptions = async (businessId?: string | null): Promise<ServiceSubscription[]> => {
  const r = await apiClient.get<unknown>("/service-hub/subscriptions", businessRequestConfig(businessId));
  return asArray<ServiceSubscription>(r.data);
};

export const requestActivation = async (payload: ActivationPayload, businessId?: string | null): Promise<ActivationResponse> => {
  const r = await apiClient.post<unknown>("/service-hub/activate", payload, businessRequestConfig(businessId));
  return unwrapApiData<ActivationResponse>(r.data);
};

export const paySubscription = async (subscriptionId: number, businessId?: string | null): Promise<PaySubscriptionResponse> => {
  const r = await apiClient.post<unknown>(`/service-hub/subscriptions/${subscriptionId}/pay`, undefined, businessRequestConfig(businessId));
  return unwrapApiData<PaySubscriptionResponse>(r.data);
};

// =========================================================
// Fund i service-hub/api.ts
// =========================================================
